import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { LayoutDashboard, Send, LogOut, ChevronDown } from "lucide-react";
import { cPath } from "../../constants";
import { logoutUser } from "../../store/actions/authActions";

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  min-width: 2.5rem;
  height: 2.5rem;
  padding: 0 0.35rem;
  border-radius: 9999px;
  border: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.2);
  background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.08);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  cursor: pointer;
  &:hover {
    background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.14);
  }
`;

const Chev = styled(ChevronDown)`
  display: none;
  @media (min-width: 480px) {
    display: block;
    margin-left: -0.1rem;
  }
`;

const Menu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 0.35rem);
  min-width: 12rem;
  padding: 0.35rem 0;
  background: hsl(${({ theme }) => theme.colors.card});
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.cardMd};
  z-index: 80;
`;

const Item = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.foreground});
  text-decoration: none;
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted});
  }
`;

const ItemBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  &:hover {
    background: hsl(${({ theme }) => theme.colors.couponMaroon} / 0.06);
  }
`;

const Initial = styled.span`
  font-weight: 700;
  font-size: 0.9375rem;
`;

export default function NavbarProfileMenu({ user, onNavigate }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const initial = (user?.name || user?.email || "U").trim().slice(0, 1).toUpperCase();

  const close = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        <Initial>{initial}</Initial>
        <Chev size={14} strokeWidth={2} />
      </Trigger>
      {open ? (
        <Menu role="menu">
          <Item to={cPath("dashboard")} onClick={close} role="menuitem">
            <LayoutDashboard size={16} strokeWidth={2} />
            Dashboard
          </Item>
          <Item to={cPath("submit-deal")} onClick={close} role="menuitem">
            <Send size={16} strokeWidth={2} />
            Submit a deal
          </Item>
          <ItemBtn
            type="button"
            role="menuitem"
            onClick={() => {
              dispatch(logoutUser());
              close();
            }}
          >
            <LogOut size={16} strokeWidth={2} />
            Sign out
          </ItemBtn>
        </Menu>
      ) : null}
    </Wrap>
  );
}
